import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";

import ai from "../../../helpers/axios";
import { isTripLockedForUser } from "../../../helpers/is-trip-editable";
import Loader from "../../loader";
import IntroBlock from "./intro-block";
import ItineraryConfirmationBlock from "./itinerary-confirmation-block";
import NotesMessagesBlock from "./notes-messages-block";
import ContractsBlock from "./contracts-block";
import ClientPaymentsBlock from "./client-payments-block";
import OfrsBlock from "./ofrs-block";
import OperatorPaymentsBlock from "./operator-payments-block";
import ClientInvoiceSummaryBlock from "./client-invoice-summary-block";
import OperatorRatingsBlock from "./operator-ratings-block";
import IncidentalsBlock from "./incidentals-block";
import CommissionBreakdownBlock from "./commission-breakdown-block";
import VerificationBlock from "./verification-block";

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged":
      return { ...state, [action.field]: action.value };
    case "dataBeganDownloading":
      return { ...state, loading: true };
    case "dataDownloaded":
      return {
        ...state,
        loading: false,
        data: action.payload?.data || null,
        dataDownloadedTimestamp: Date.now(),
      };
    case "dataDownloadFailed":
      return { ...state, loading: false };
    default:
      return state;
  }
};

const initialState = {
  data: null,
  loading: true,
  dataDownloadedTimestamp: Date.now(),
  refreshDataToken: null,
};

const ROW_PATCH_BY_MODE = {
  verify: { verified: true },
  unverify: { verified: false },
  lock: { lockedComputed: true },
  unlock: { lockedComputed: false },
};

const TripVerificationExpanded = ({ data, onRowChanged }) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [incidentalsIncomplete, setIncidentalsIncomplete] = useState(false);
  const [incidentalsChangedAt, setIncidentalsChangedAt] = useState(null);
  const handleIncidentalsChanged = useCallback(() => setIncidentalsChangedAt(Date.now()), []);

  const userRoles = (user && user["https://new.evojets.com/roles"]) || [];
  const superAdmin = userRoles.some((role) => role === "Super Admin");
  const verificationAdmin = !!user?.["https://new.evojets.com/appMetadata"]?.isVerificationAdmin;
  const readOnly = isTripLockedForUser(state.data, superAdmin, verificationAdmin);

  const tripId = data?._id;

  const getData = useCallback(async () => {
    if (!tripId) return;

    dispatch({ type: "dataBeganDownloading" });

    try {
      const token = await getAccessTokenSilently();
      const response = await ai.auth(token).get(`/api/trips/${tripId}`);
      dispatch({ type: "dataDownloaded", payload: { data: response?.data } });
    } catch (error) {
      console.error("Error fetching trip verification detail:", error);
      toast.error("Failed to load trip");
      dispatch({ type: "dataDownloadFailed" });
    }
  }, [tripId, getAccessTokenSilently]);

  const handleTripChanged = useCallback(
    ({ mode }) => {
      getData();
      onRowChanged?.(tripId, ROW_PATCH_BY_MODE[mode] || {});
    },
    [getData, onRowChanged, tripId],
  );

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (state.refreshDataToken) getData();
  }, [state.refreshDataToken, getData]);

  const debouncedRefresh = useDebouncedCallback(() => getData(), 100);

  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === "visible") {
        debouncedRefresh();
      }
    };
    document.addEventListener("visibilitychange", handleVisible);
    return () => {
      document.removeEventListener("visibilitychange", handleVisible);
      debouncedRefresh.cancel();
    };
  }, [debouncedRefresh]);

  if (state.loading) {
    return (
      <div className="tv-expanded tv-expanded-loading">
        <Loader/>
      </div>
    );
  }

  return (
    <div className="tv-expanded">
      <div className="tv-full">
        <IntroBlock/>
      </div>

      <div className="tv-pair">
        <ItineraryConfirmationBlock tripId={tripId} trip={state.data} readOnly={readOnly}/>
        <NotesMessagesBlock tripId={tripId} trip={state.data} readOnly={readOnly}/>
      </div>
      <div className="tv-pair">
        <ContractsBlock tripId={tripId} trip={state.data} readOnly={readOnly}/>
        <ClientPaymentsBlock tripId={tripId} trip={state.data} readOnly={readOnly}/>
      </div>
      <div className="tv-pair">
        <OfrsBlock tripId={tripId} trip={state.data} readOnly={readOnly}/>
        <OperatorPaymentsBlock tripId={tripId} trip={state.data} readOnly={readOnly}/>
      </div>
      <div className="tv-pair">
        <OperatorRatingsBlock
          tripId={tripId}
          trip={state.data}
          onChanged={getData}
          readOnly={readOnly}
        />
        <ClientInvoiceSummaryBlock tripId={tripId} trip={state.data} readOnly={readOnly}/>
      </div>
      <div className="tv-pair">
        <IncidentalsBlock
          tripId={tripId}
          trip={state.data}
          onIncompleteChange={setIncidentalsIncomplete}
          onChanged={handleIncidentalsChanged}
          readOnly={readOnly}
        />
        <CommissionBreakdownBlock
          tripId={tripId}
          trip={state.data}
          canEdit={superAdmin}
          refreshKey={`${state.dataDownloadedTimestamp}-${incidentalsChangedAt}`}
        />
      </div>

      <div className="tv-full">
        <VerificationBlock
          tripId={tripId}
          trip={state.data}
          onChanged={handleTripChanged}
          superAdmin={superAdmin}
          verificationAdmin={verificationAdmin}
          incidentalsIncomplete={incidentalsIncomplete}
        />
      </div>
    </div>
  );
};

export default TripVerificationExpanded;
